import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AmarBankService from "service/amarBank/AmarBankService";
import { BankAccount } from "domain/payload/response/bankamar/BankAccountResp";
import BankAccountDetailModal from "./modal/BankAccountDetailModal";
import CustomDropdown, { DropdownOption } from "component/CustomDropdown";
import { PageSizeOption } from "domain/constant/PageSizeOption";

type StatusType = "accepted" | "rejected" | null;

const BankAccountRegistration: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);

  const [data, setData] = useState<BankAccount[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusTerm, setStatusTerm] = useState<StatusType>(null);
  const [rejectReasonTerm, setRejectReasonTerm] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BankAccount | null>(null);

  const [loading, setLoading] = useState(false);
  const [isSearchButtonDisabled, setIsSearchButtonDisabled] = useState(true);

  const STATUS_OPTIONS: DropdownOption[] = [
    { id: null, name: 'All Status' },
    { id: "accepted", name: 'Accepted' },
    { id: "rejected", name: 'Rejected' },
  ];

  const REJECT_REASON_OPTIONS: DropdownOption[] = [
    { id: null, name: 'All Reject Reasons' },
    { id: "selfieHoldingIdCard", name: 'Selfie holding ID Card' },
    { id: "editedSelfie", name: 'Edited Selfie' },
    { id: "selfieCapturedByOther", name: 'Selfie Captured By Other' },
    { id: "zeroLiveness", name: 'Zero Liveness' },
    { id: "multipleFace", name: 'Multiple Face' },
    { id: "sleepingSelfie", name: 'Sleeping Selfie' },
    { id: "selfieNotMatchEKTP", name: 'Selfie Not Match e-KTP' },
    { id: "uploadedPhoto", name: 'Uploaded Photo' },
    { id: "screenPhoto", name: 'Screen Photo' },
    { id: "blankSelfie", name: 'Blank Selfie' },
  ];

  const amarBankService = AmarBankService();

  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res = await amarBankService.getList(
        token,
        currentPage,
        pageSize,
        searchTerm || null,
        statusTerm,
        rejectReasonTerm || null
      );

      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data bank account';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
      setIsSearchButtonDisabled(true);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, statusTerm]);

  useEffect(() => {
    setIsSearchButtonDisabled(false);
  }, [searchTerm, rejectReasonTerm]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSearch();
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchData();
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Bank Account Registration</h5>
            <div>
              <p>Monitoring Account Registration</p>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="p-4" style={{ background: "white" }}>
            {/* FILTER */}
            <div className="d-flex mb-3 gap-2">
              <form className="d-flex me-2" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 120 }}
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-small-compact h-100 p-4"
                  disabled={isSearchButtonDisabled}
                >
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <CustomDropdown
                value={statusTerm}
                options={STATUS_OPTIONS}
                onChange={(opt) => setStatusTerm((opt.id || null) as StatusType)}
                containerClassName="me-1"
                align="right"
                buttonStyle={{ width: "160" }}
              />

              <CustomDropdown
                value={rejectReasonTerm}
                options={REJECT_REASON_OPTIONS}
                onChange={(opt) => setRejectReasonTerm((opt.id || null) as string | null)}
                containerClassName="me-1"
                align="right"
                buttonStyle={{ width: "220" }}
              />
            </div>

            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Account Number</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Timestamp</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {!loading ? (
                  data.length > 0 ? (
                    data.map((item) => (
                      <tr key={item.id}>
                        <td>{item.partnerCustomerId}</td>
                        <td>{item.partnerCustomerEmail}</td>
                        <td>{item.accountNumber}</td>
                        <td>{item.status}</td>
                        <td>{item.type}</td>
                        <td>{item.timestamp}</td>
                        <td>
                          <button
                            className="btn btn-outline-info btn-small-compact border-0"
                            onClick={() => {
                              setSelectedItem(item);
                              setShowModal(true);
                            }}
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        Tidak ada data
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="d-flex justify-content-end align-items-center mt-3">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(
                    currentPage * pageSize,
                    totalRecords
                  )} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="pagination mb-0">
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(1)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>
                        {currentPage}
                      </span>
                    </li>

                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(totalPages)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && selectedItem && (
        <BankAccountDetailModal
          item={selectedItem}
          onClose={() => {
            setShowModal(false);
          }}
        />
      )}
    </div>
  );
};

export default BankAccountRegistration;
